'use client';

// Inline Phosphor glyph (no react-icons index chunk), as in components/ai-chat/AiChatFab.
import { css } from 'styled-system/css';

import { ChatCircleDotsIcon } from '@/components/atoms/PhosphorIcons/PhosphorIcons';

type Props = {
  isOpen: boolean;
  onClick: () => void;
  label: string;
  ariaLabel: string;
};

// EASE = cubic-bezier(0.65, 0, 0.35, 1); z-index = AI_CHAT_Z_INDEX (1200) — literals for Panda.
const fabClass = css({
  animation: 'sh-fab-in 0.22s ease-out',
  position: 'fixed',
  bottom: { base: '18px', md: '28px' },
  right: { base: '18px', md: '28px' },
  height: { base: '52px', md: '60px' },
  px: { base: 0, md: '17px' },
  w: { base: '52px', md: 'auto' },
  borderRadius: { base: '26px', md: '30px' },
  bg: 'lime.500',
  color: 'surface.500',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  cursor: 'pointer',
  zIndex: 1200,
  boxShadow: '0 12px 28px rgba(6,3,15,0.45), 0 4px 10px rgba(6,3,15,0.35), inset 0 0 0 1px rgba(255,255,255,0.18)',
  opacity: 1,
  transform: 'none',
  visibility: 'visible',
  pointerEvents: 'auto',
  transition:
    'opacity 0.22s ease-out, visibility 0.22s, transform 0.25s cubic-bezier(0.65, 0, 0.35, 1), box-shadow 0.25s cubic-bezier(0.65, 0, 0.35, 1), padding 0.4s cubic-bezier(0.65, 0, 0.35, 1)',
  _hover: {
    transform: 'translateY(-3px)',
    boxShadow:
      '0 18px 36px rgba(6,3,15,0.55), 0 6px 16px rgba(6,3,15,0.4), inset 0 0 0 1px rgba(255,255,255,0.22), 0 0 28px rgba(217,252,89,0.35)',
    paddingLeft: { base: 0, md: '22px' },
  },
  _active: { transform: 'translateY(-1px)' },
  '&:hover .fab-label': { maxWidth: '240px', opacity: 1, marginRight: '10px' },
  // While the panel is open the FAB fades out in place and stops taking clicks or focus.
  '&[data-open]': {
    opacity: 0,
    transform: 'translateY(20px) scale(0.6)',
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const labelClass = css({
  display: { base: 'none', md: 'block' },
  fontSize: '15px',
  fontWeight: '500',
  lineHeight: '1',
  whiteSpace: 'nowrap',
  maxWidth: '0',
  opacity: 0,
  overflow: 'hidden',
  mr: 0,
  letterSpacing: '-0.005em',
  transition:
    'max-width 0.4s cubic-bezier(0.65, 0, 0.35, 1), opacity 0.25s ease 0.05s, margin-right 0.4s cubic-bezier(0.65, 0, 0.35, 1)',
});

const iconClass = css({ flexShrink: 0, w: { base: '28px', md: '32px' }, h: { base: '28px', md: '32px' } });

/** components/ai-chat/AiChatFab without Chakra/Emotion/next-i18next (labels come from the server). */
export const AiChatFab = ({ isOpen, onClick, label, ariaLabel }: Props) => (
  <button
    type="button"
    aria-label={ariaLabel}
    aria-expanded={isOpen}
    onClick={onClick}
    className={fabClass}
    data-open={isOpen ? '' : undefined}
    // Hidden by the visual-regression suite (e2e-visual/utils/prepare-page.ts): own entrance animation.
    data-testid="ai-chat-fab"
  >
    {/* Label - only visible on desktop hover */}
    <p className={`fab-label ${labelClass}`}>{label}</p>
    <div className={iconClass}>
      <ChatCircleDotsIcon size="100%" />
    </div>
  </button>
);
